import clsx from 'clsx'
import { Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, Outlet, useLocation, useMatches, useNavigate } from 'react-router-dom'

import { logout } from '@/shared/api/session'
import { useBreakpoint } from '@/shared/hooks/useBreakpoint'
import { Avatar, Button, LanguageSwitcher, Spinner, ThemeToggle } from '@/shared/ui'
import AppFooter from '@/shared/ui/AppFooter'
import { BrandMark, Logo } from '@/shared/ui/brand'
import { selectIsAuthenticated, useSessionStore } from '@/shared/store/session'

function MenuIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  )
}

function CloseIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  )
}

function ChevronIcon({ flipped, className, ...props }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={clsx('transition-transform', flipped && 'rotate-180', className)}
      {...props}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 6l-6 6 6 6" />
    </svg>
  )
}

function SearchIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <circle cx="11" cy="11" r="7" />
      <path strokeLinecap="round" d="M21 21l-4.35-4.35" />
    </svg>
  )
}

function UserIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <circle cx="12" cy="8" r="3.5" />
      <path strokeLinecap="round" d="M4.5 20c1.6-3.6 5-5 7.5-5s5.9 1.4 7.5 5" />
    </svg>
  )
}

function ApplicationsIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <rect x="4" y="6" width="16" height="14" rx="2" />
      <path strokeLinecap="round" d="M9 6V5a3 3 0 0 1 6 0v1M9 12h6M9 16h4" />
    </svg>
  )
}

function BriefcaseIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <rect x="3" y="7" width="18" height="12" rx="2" />
      <path
        strokeLinecap="round"
        d="M8 7V5.5A1.5 1.5 0 0 1 9.5 4h5A1.5 1.5 0 0 1 16 5.5V7M3 12h18"
      />
    </svg>
  )
}

function TagIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path
        strokeLinejoin="round"
        strokeLinecap="round"
        d="M11.5 4H6a2 2 0 0 0-2 2v5.5a2 2 0 0 0 .59 1.41l8.5 8.5a2 2 0 0 0 2.82 0l5.5-5.5a2 2 0 0 0 0-2.82l-8.5-8.5A2 2 0 0 0 11.5 4Z"
      />
      <circle cx="8.5" cy="8.5" r="1.25" fill="currentColor" stroke="none" />
    </svg>
  )
}

function UsersIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <circle cx="9" cy="8" r="3" />
      <path strokeLinecap="round" d="M2.5 20c1.2-3.4 3.9-5 6.5-5s5.3 1.6 6.5 5" />
      <path strokeLinecap="round" d="M16 4.2a3 3 0 0 1 0 5.6M18.5 20c-.5-2.1-1.5-3.6-3-4.5" />
    </svg>
  )
}

/** Los items de navegación son los mismos para los tres niveles del shell. */
function useNavItems({ isStaff, isAuthenticated }) {
  const { t } = useTranslation()
  const primary = [
    { to: '/vacantes', end: true, icon: BriefcaseIcon, label: t('nav.jobs') },
    // Los planes son de contratación de la plataforma: solo le sirven a una
    // empresa ya autenticada, no a un aspirante ni a un visitante anónimo.
    ...(isStaff ? [{ to: '/empresa/planes', icon: TagIcon, label: t('nav.plans') }] : []),
    { to: '/aliados', icon: UsersIcon, label: t('nav.partners') },
  ]
  const account = isStaff
    ? [{ to: '/empresa', icon: UserIcon, label: t('nav.company') }]
    : isAuthenticated
      ? [{ to: '/mis-postulaciones', icon: ApplicationsIcon, label: t('nav.myApplications') }]
      : [{ to: '/login', icon: UserIcon, label: t('nav.access') }]
  return [...primary, ...account]
}

/** Item del sidebar/rail/panel: mismas clases `.humania-nav__item` que el panel de empresa. */
function SidebarNavItem({ item, iconOnly }) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={iconOnly ? item.label : undefined}
      className={({ isActive }) => clsx('humania-nav__item', isActive && 'is-active')}
    >
      <Icon className="size-5 shrink-0" />
      <span className={iconOnly ? 'sr-only' : ''}>{item.label}</span>
    </NavLink>
  )
}

/** Identidad de quien tiene la sesión abierta, junto al botón de salir. */
function UserChip({ name, className }) {
  return (
    <span
      className={clsx(
        'flex min-w-0 items-center gap-2 rounded-full bg-ink-50 py-1 pl-1 pr-3',
        className
      )}
    >
      <Avatar name={name} className="size-7 text-[11px]" />
      <span className="max-w-40 truncate text-sm font-medium text-ink-900">{name}</span>
    </span>
  )
}

function BottomNavTab({ item }) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => clsx('humania-bottomnav__tab', isActive && 'is-active')}
    >
      <Icon className="size-5 shrink-0" />
      <span>{item.label}</span>
    </NavLink>
  )
}

/**
 * Shell de 3 niveles, mismo patrón que Safyro Finanzas (app/AppShell.tsx) y
 * que `CompanyLayout`: sidebar con etiquetas en desktop, rail de íconos en
 * tablet, BottomNavBar en mobile. Sirve tanto al portal público de vacantes
 * como, ya autenticado, al aspirante o (si es staff) de paso hacia el panel
 * de empresa.
 */
export default function PublicLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const bp = useBreakpoint()
  const [navOpen, setNavOpen] = useState(false)
  // Solo aplica en desktop: en tablet/mobile el shell ya es angosto o vive en
  // el panel superpuesto, no hace falta otro control para lo mismo.
  const [desktopCollapsed, setDesktopCollapsed] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  // La lista de vacantes ocupa todo el ancho porque lleva su propia columna de
  // filtros a la izquierda; el resto de páginas es texto y se sigue leyendo
  // mejor en una medida corta. Lo declara la ruta en `handle`, que es lo que
  // ve el layout sin tener que saberse los paths de memoria.
  const isFullWidth = useMatches().some((match) => match.handle?.fullWidth)
  const isAuthenticated = useSessionStore(selectIsAuthenticated)
  // Un aspirante que aún no se ha postulado no tiene nombre: el correo es lo
  // único que identifica de quién es la sesión abierta.
  const nombre = useSessionStore((state) => state.user?.full_name || state.user?.email)
  // Solo el staff tiene rol. A un aspirante no se le ofrece el panel de
  // empresa: el guard lo rebotaría y el enlace no significa nada para él.
  const isStaff = useSessionStore((state) => Boolean(state.user?.role))
  const navItems = useNavItems({ isStaff, isAuthenticated })

  useEffect(() => setNavOpen(false), [location.pathname, bp])

  // El aspirante entra por enlace mágico y no sale nunca de este layout: si
  // aquí no hay salida, no tiene ninguna, y en un equipo compartido la sesión
  // queda abierta para quien se siente después.
  const handleLogout = async () => {
    await logout()
    navigate('/', { replace: true })
  }

  const onSearchSubmit = (event) => {
    event.preventDefault()
    const trimmed = searchTerm.trim()
    navigate(trimmed ? `/vacantes?q=${encodeURIComponent(trimmed)}` : '/vacantes')
  }

  return (
    <div className="humania-page flex min-h-full">
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-(--humania-surface) focus:px-3 focus:py-2"
      >
        Ir al contenido
      </a>

      {bp === 'desktop' ? (
        <aside
          className={clsx(
            'humania-sidebar hidden shrink-0 flex-col md:flex',
            desktopCollapsed ? 'w-22 px-2' : 'w-64'
          )}
        >
          <div className="mb-7">
            <div className={clsx('flex items-center', desktopCollapsed && 'justify-center')}>
              <Link to="/" aria-label={t('app.name')} className="min-w-0">
                {desktopCollapsed ? <BrandMark className="rounded-xl" /> : <Logo className="h-6" />}
              </Link>
            </div>
            {/* Distingue de un vistazo en cuál de los dos perfiles se está: el
                mismo shell de sidebar sirve tanto al portal de aspirantes como,
                cuando quien entra es staff, al panel de empresa. Sin sesión no
                hay perfil que anunciar. */}
            {isAuthenticated && !desktopCollapsed ? (
              <span className="mt-2 inline-block rounded-full bg-ink-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                {isStaff ? t('nav.profileCompany') : t('nav.profileCandidate')}
              </span>
            ) : null}
          </div>

          <nav className="humania-nav">
            {navItems.map((item) => (
              <SidebarNavItem key={item.to} item={item} iconOnly={desktopCollapsed} />
            ))}
          </nav>
        </aside>
      ) : null}

      {bp === 'tablet' ? (
        <nav className="humania-navrail" aria-label={t('nav.menu')}>
          <Link to="/" aria-label={t('app.name')} className="mx-auto">
            <BrandMark className="rounded-xl" />
          </Link>
          {navItems.map((item) => (
            <SidebarNavItem key={item.to} item={item} iconOnly />
          ))}
        </nav>
      ) : null}

      {bp !== 'desktop' && navOpen ? (
        <div className="humania-nav-overlay" onClick={() => setNavOpen(false)}>
          <nav
            className="humania-nav-panel"
            aria-label={t('nav.menu')}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between px-2">
              <Logo className="h-7" />
              <button
                type="button"
                aria-label={t('nav.collapse')}
                onClick={() => setNavOpen(false)}
                className="grid size-8 shrink-0 place-items-center rounded-lg text-ink-500 transition hover:bg-ink-50 hover:text-ink-900"
              >
                <CloseIcon className="size-5" />
              </button>
            </div>
            <nav className="humania-nav">
              {navItems.map((item) => (
                <SidebarNavItem key={item.to} item={item} />
              ))}
            </nav>
            <div className="mt-auto space-y-3 border-t border-ink-100 px-2 pt-4">
              {isAuthenticated ? <UserChip name={nombre} /> : null}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <ThemeToggle />
                  <LanguageSwitcher />
                </div>
                {isAuthenticated ? (
                  <Button variant="ghost" onClick={handleLogout}>
                    {t('nav.logout')}
                  </Button>
                ) : null}
              </div>
            </div>
          </nav>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-stretch border-b border-(--humania-shell-border) bg-(--humania-surface)">
          {bp === 'desktop' ? (
            // Pegado al borde del sidebar (sin el padding del resto del
            // header) y del mismo alto, como una pestaña de esa misma pieza.
            <button
              type="button"
              aria-label={desktopCollapsed ? t('nav.expand') : t('nav.collapse')}
              onClick={() => setDesktopCollapsed((collapsed) => !collapsed)}
              className="grid w-12 shrink-0 place-items-center border-r border-(--humania-shell-border) text-ink-500 transition hover:bg-ink-50 hover:text-ink-900"
            >
              <ChevronIcon flipped={desktopCollapsed} className="size-4" />
            </button>
          ) : null}

          <div className="mx-auto flex min-w-0 flex-1 max-w-[1600px] items-center gap-3 px-4 py-3 sm:gap-4 sm:px-6">
            {bp !== 'desktop' ? (
              <button
                type="button"
                aria-label={t('nav.menu')}
                aria-expanded={navOpen}
                onClick={() => setNavOpen((open) => !open)}
                className="grid size-9 shrink-0 place-items-center rounded-lg border border-ink-200 text-ink-600 transition hover:bg-ink-50 hover:text-ink-900"
              >
                <MenuIcon className="size-5" />
              </button>
            ) : null}

            <form onSubmit={onSearchSubmit} role="search" className="min-w-0 flex-1">
              <label htmlFor="global-search" className="sr-only">
                {t('nav.searchPlaceholder')}
              </label>
              <div className="flex items-center rounded-lg border border-ink-200 bg-(--humania-bg) pl-4 pr-1.5">
                <input
                  id="global-search"
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t('nav.searchPlaceholder')}
                  className="min-w-0 flex-1 bg-transparent py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none"
                />
                <button
                  type="submit"
                  aria-label={t('jobs.search')}
                  className="grid size-8 shrink-0 place-items-center rounded-md bg-brand-600 text-white transition hover:bg-brand-700"
                >
                  <SearchIcon className="size-4" />
                </button>
              </div>
            </form>

            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <ThemeToggle />
              <div className="hidden sm:block">
                <LanguageSwitcher />
              </div>
              {isAuthenticated ? <UserChip name={nombre} className="hidden md:flex" /> : null}
              {isAuthenticated ? (
                <Button variant="ghost" onClick={handleLogout} className="hidden sm:inline-flex">
                  {t('nav.logout')}
                </Button>
              ) : null}
            </div>
          </div>
        </header>

        <main
          id="contenido"
          className={clsx(
            'mx-auto w-full flex-1 px-4 py-10',
            isFullWidth ? 'max-w-[1600px]' : 'max-w-5xl',
            bp === 'mobile' && 'pb-24'
          )}
        >
          <Suspense fallback={<Spinner />}>
            <Outlet />
          </Suspense>
        </main>

        {bp !== 'mobile' ? <AppFooter /> : null}
      </div>

      {bp === 'mobile' ? (
        <nav className="humania-bottomnav" aria-label={t('nav.menu')}>
          {navItems.map((item) => (
            <BottomNavTab key={item.to} item={item} />
          ))}
        </nav>
      ) : null}
    </div>
  )
}
